import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useSystemDocumentsStore } from '@/stores/systemDocuments';
import { toast } from '@/stores/toast';
import i18n from '@/i18n';
import { resolveApiBaseUrl } from '@/utils/resolveApiBaseUrl';
import { reportFrontendDiagnostic } from '@/lib/frontendDiagnostics';

const getAccessToken = () => useAuthStore.getState().token;
const BASE_URL = resolveApiBaseUrl();
let isHandlingUnauthorized = false;

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    config.headers['X-Client-Source'] = 'WEB';

    if (config.url !== '/auth/login' && accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return Promise.resolve(config);
  },
  (error) => {
    return Promise.reject(error);
  },
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response) {
      const originalConfig = error.config;
      const { status, data } = error.response;
      const responseCode = data?.code || data?.error;
      const requestUrl = String(originalConfig?.url || '');

      if (
        status === 428 &&
        responseCode === 'SYSTEM_DOCUMENT_ACCEPTANCE_REQUIRED'
      ) {
        useSystemDocumentsStore.getState().setAcceptanceRequiredByApi(true);
        return Promise.reject(error);
      }

      if (
        status === 428 &&
        (responseCode === 'ESTABLISHMENT_CONTEXT_NOT_SELECTED' ||
          responseCode === 'ESTABLISHMENT_CONTEXT_INVALID') &&
        requestUrl !== '/api/user/context/establishment' &&
        requestUrl !== 'api/user/context/establishment'
      ) {
        useAuthStore.getState().requireContextSelection();
        return Promise.reject(error);
      }

      if (
        originalConfig.url !== '/api/auth/login' &&
        originalConfig.url !== 'api/auth/login' &&
        originalConfig.url !== '/api/auth/logout' &&
        originalConfig.url !== 'api/auth/logout' &&
        originalConfig.url !== '/api/auth/change-password' &&
        originalConfig.url !== 'api/auth/change-password' &&
        originalConfig.url !== '/api/auth/activate-account' &&
        originalConfig.url !== 'api/auth/activate-account' &&
        status === 401
      ) {
        if (isHandlingUnauthorized) {
          return Promise.reject(error);
        }

        isHandlingUnauthorized = true;
        reportFrontendDiagnostic(
          'session_expired_redirect',
          new Error('An authenticated API request returned HTTP 401'),
          {
            from: window.location.pathname,
            to: '/session/login',
            reason: 'api_unauthorized_response',
          },
        );
        useAuthStore.getState().expireSession();

        toast({
          title:
            i18n.t('auth.session_expired') ||
            'Sua sessão expirou. Por favor, realize o login novamente',
          variant: 'destructive',
        });
      }

      if (status === 403 && String(responseCode).toLowerCase() === 'forbidden') {
        toast({
          title:
            i18n.t('auth.permission_denied') || 'Acesso negado',
          description:
            i18n.t('auth.permission_denied_description') ||
            'Você não tem permissão para acessar esta funcionalidade. Contate o suporte.',
          variant: 'destructive',
        });
      }

      return Promise.reject(error);
    } else {
      return Promise.reject(error);
    }
  },
);

export { api };
export default api as typeof api & { fetch: typeof api };
